// src/pages/About.js
import React from 'react';
import WebpageShell from './WebpageShell';
import HelmetTemplate from '../utils/HelmetTemplate';

import aboutBackground from '../images/about_background.jpg';
import AlpImg from '../images/AlpImg.webp';
import EnesImg from '../images/EnesImg.webp';
import linkedinLogo from "../images/linkedinlogo.svg";
import xLogo from "../images/xlogo.svg";

function SocialIcon({ href, src, alt, size = 20 }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center justify-center rounded-full hover:opacity-90 transition"
      aria-label={alt}
    >
      <img src={src} alt={alt} style={{ width: size, height: size }} />
    </a>
  );
}

function FounderCard({ img, name, role, paragraphs, xUrl, liUrl }) {
  return (
    <article className="flex flex-col items-center text-center bg-white rounded-2xl md:border border-black/10 p-5 md:p-6">
      <img
        src={img}
        alt={name}
        className="
          w-full rounded-2xl mb-4 object-cover mx-auto
          max-w-[200px] md:h-auto md:object-contain
        "
      />
      <h3 className="text-lg font-extrabold text-gray-900">{name}</h3>
      <p className="text-gray-500 mt-1 mb-4 text-sm font-normal">{role}</p>
      {paragraphs.map((t, i) => (
        <p key={i} className="text-gray-600 font-light tracking-tight mb-4">
          {t}
        </p>
      ))}
      <div className="mt-auto flex items-center gap-4">
        {xUrl && <SocialIcon href={xUrl} src={xLogo} alt="X" size={20} />}
        {liUrl && <SocialIcon href={liUrl} src={linkedinLogo} alt="LinkedIn" size={28} />}
      </div>
    </article>
  );
}

export default function About() {
  return (
    <WebpageShell
      navProps={{ addAnnouncementBar: false, isRecPrototypePage: false }}
      showCTA={true}
    >
      <HelmetTemplate
        title="About Match Medic"
        description="Learn about Match Medic's mission to provide the best possible guidance to patients for deciding on a hair transplant clinic. Meet our team and discover our story."
        canonicalUrl="https://matchmedic.com/about"
      />
      {/* AboutMatchMedic (hero) */}
      <section className="md:w-4/5 md:mx-auto md:mt-6">
        {/* Mobile: full-bleed wrapper; Desktop: contained */}
        <div className="mx-[calc(50%-50vw)] w-screen md:mx-0 md:w-auto">
          <div
            className="relative bg-cover bg-center bg-no-repeat md:rounded-2xl"
            style={{ backgroundImage: `url(${aboutBackground})` }}
          >
            <div className="py-10 md:py-14 md:px-12">
              <div className="bg-white/80 backdrop-blur-[1px] max-w-4xl mx-auto p-6 md:p-10 md:rounded-xl shadow-lg text-center">
                <h1 className="mb-5 text-3xl font-extrabold text-gray-900">
                  Who We Are
                </h1>
                <p className="text-lg tracking-tight mb-4 text-gray-600 font-light">
                  We are a team of highly motivated people with a simple purpose:&nbsp;
                  <strong>provide the best possible guidance to patients for deciding on a hair transplant clinic.</strong>
                </p>
                <p className="text-lg tracking-tight mb-4 text-gray-600 font-light">
                  We believe the current system to find the right clinic for hair transplants is overly cumbersome and overwhelming.
                  There is just too much advertisement and misinformation to make an informed decision quickly.
                </p>
                <p className="text-lg tracking-tight mb-1 text-gray-600 font-light">
                  We created Match Medic so people can see their options based on authentic patient experiences and proven results—not just promotions.
                  Review clinic track records, learn from similar patients, assess outcomes, and make your own choice.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="md:w-4/5 md:mx-auto mt-10 md:mt-12 mb-12">
        <h2 className="text-center text-3xl md:text-4xl font-extrabold">Meet the Team</h2>

        {/* Mobile: full-bleed for the cards background; Desktop: contained */}
        <div className="mx-[calc(50%-50vw)] w-screen md:mx-0 md:w-auto">
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-y-6 gap-x-0 md:gap-8">
            <FounderCard
              img={AlpImg}
              name="Alp Mungan"
              role="Co-Founder @ Match Medic"
              paragraphs={[
                "I used to work in the largest corporate law firm in Turkey on M&A transactions, including in healthcare. I co-founded Match Medic after seeing how chaotic and difficult the international healthcare market can be for patients.",
                "I’m primarily responsible for day-to-day operations. As a registered lawyer, I ensure our partner clinics are compliant and our users’ rights are respected. I’m obsessed with making the patient’s journey as easy and secure as possible."
              ]}
              xUrl="https://x.com/MunganAlp"
              liUrl="https://www.linkedin.com/in/alp-mungan-6877bb189/"
            />

            <FounderCard
              img={EnesImg}
              name="Enes Yıldırım"
              role="Co-Founder @ Match Medic"
              paragraphs={[
                "I began my career as an embedded software engineer in the defense industry while completing a master’s degree, focusing on complex systems.",
                "At Match Medic, I build secure, reliable, and accurate systems. I believe technology is key to making quality healthcare accessible anywhere by bridging barriers with smart, efficient solutions."
              ]}
              xUrl="https://x.com/enesfly3"
              liUrl="https://www.linkedin.com/in/enesyildirim03/"
            />
          </div>
        </div>
      </section>
    </WebpageShell>
  );
}
